import { useState, useEffect } from 'react';
import { RefreshCw, Database, CheckCircle, AlertCircle, Loader2, FolderOpen, PlayCircle, Clock, Store, ShoppingBag } from 'lucide-react';

interface DriveFile {
  id: string;
  name: string;
  size: string;
  createdTime: string;
  modifiedTime: string;
}

interface DriveStatus {
  folderId: string;
  fileCount: number;
  files: DriveFile[];
  latestFiles?: {
    salesInvPerf: DriveFile | null;
    storeSales: DriveFile | null;
  };
  hasRequiredFiles: boolean;
  monitoringActive: boolean;
  retailer?: string;
  retailerId?: string;
  companyId?: string;
  error?: string;
}

interface ProcessingStep {
  step: string;
  success: boolean;
  message?: string;
  rowCount?: number;
  error?: string;
}

interface DuplicateCheck {
  fileId: string;
  fileName: string;
  fiscalWeek: string;
  existsInDatabase: boolean;
  retailerName?: string;
  rowCounts?: {
    kpi?: number;
    sku?: number;
    store?: number;
  };
}

type RetailerTab = 'Ulta' | 'Macys' | 'JCPenney';

export default function AdminDataMonitor() {
  const [activeRetailer, setActiveRetailer] = useState<RetailerTab>('Ulta');
  const [driveStatus, setDriveStatus] = useState<DriveStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [processingResults, setProcessingResults] = useState<ProcessingStep[]>([]);
  const [error, setError] = useState<string>('');
  const [lastCheck, setLastCheck] = useState<Date>(new Date());
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateCheck[]>([]);

  // Load Google Drive status on mount and when retailer changes
  useEffect(() => {
    loadDriveStatus();
    const interval = setInterval(loadDriveStatus, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [activeRetailer]);

  const loadDriveStatus = async () => {
    setLoading(true);
    try {
      // Call appropriate API based on selected retailer
      let endpoint = '/api/google-drive/status';
      if (activeRetailer === 'Macys') {
        endpoint = '/api/google-drive/macys-status';
      } else if (activeRetailer === 'JCPenney') {
        endpoint = '/api/google-drive/jcp-status';
      }
      
      const response = await fetch(endpoint);
      
      // Handle case where folder is not configured
      if (!response.ok) {
        const errorData = await response.json();
        if (activeRetailer === 'Macys' && errorData.error?.includes('GOOGLE_DRIVE_MACYS_FOLDER_ID')) {
          setDriveStatus({
            folderId: '',
            fileCount: 0,
            files: [],
            hasRequiredFiles: false,
            monitoringActive: false,
            retailer: 'Macys',
            error: 'Macy\'s Google Drive folder not configured. Please set GOOGLE_DRIVE_MACYS_FOLDER_ID in Vercel.'
          });
          setLastCheck(new Date());
          setSelectedFiles(new Set());
          return;
        }
        if (activeRetailer === 'JCPenney' && (errorData.error?.includes('GOOGLE_DRIVE_JCP_FOLDER_ID') || !errorData.folderId)) {
          setDriveStatus({
            folderId: '',
            fileCount: 0,
            files: [],
            hasRequiredFiles: false,
            monitoringActive: false,
            retailer: 'JCPenney',
            error: 'JCPenney Google Drive folder not configured. Please set GOOGLE_DRIVE_JCP_FOLDER_ID in Vercel.'
          });
          setLastCheck(new Date());
          setSelectedFiles(new Set());
          return;
        }
        throw new Error(errorData.message || 'Failed to load drive status');
      }
      
      const data = await response.json();
      setDriveStatus(data);
      setLastCheck(new Date());
      setError('');
      
      // Auto-select files based on retailer
      if (data.files && data.files.length > 0) {
        if (activeRetailer === 'Macys') {
          // For Macy's: auto-select the most recent BUTTAH files
          const macysFiles = data.files.filter((f: DriveFile) => {
            const name = f.name?.toUpperCase() || '';
            return name.includes('BUTTAH') && name.endsWith('.XLSX');
          });
          
          // Sort by modified time descending and select the 2 most recent
          const sortedMacys = macysFiles.sort((a: DriveFile, b: DriveFile) => 
            new Date(b.modifiedTime).getTime() - new Date(a.modifiedTime).getTime()
          );
          
          const newSelection = new Set<string>();
          sortedMacys.slice(0, 2).forEach((f: DriveFile) => newSelection.add(f.id));
          setSelectedFiles(newSelection);
        } else if (activeRetailer === 'JCPenney') {
          // For JCPenney: auto-select the most recent matching SKU and Store files
          // Helper to check week suffix patterns: _w##, _FW##, _FW_##
          const hasWeekSuffix = (name: string): boolean => {
            return /_W\d+\.XLSX$/i.test(name) || /_FW_?\d+\.XLSX$/i.test(name);
          };
          // Check if file has "L" suffix before week (Location = Store Sales)
          const hasLocationSuffix = (name: string): boolean => {
            return /L[_](?:FW[_]?|W)\d+\.XLSX$/i.test(name) || /L_W\d+\.XLSX$/i.test(name);
          };
          
          const isJcpSkuFile = (name: string): boolean => {
            const upper = name.toUpperCase();
            if (!upper.endsWith('.XLSX')) return false;
            if (hasLocationSuffix(name)) return false; // L suffix = Store Sales
            if (upper.includes('_JCP_') && upper.includes('_SKU_')) return true;
            if (/^\d+_SKU/i.test(name) && upper.includes('REPORT')) return true;
            if (upper.includes('SKU') && hasWeekSuffix(name)) return true;
            if (upper.includes('INVENTORY REPORTS') && hasWeekSuffix(name)) return true;
            if (/^\d+_/.test(name) && upper.includes('INVENTORY REPORTS')) return true;
            return false;
          };
          
          const isJcpStoreFile = (name: string): boolean => {
            const upper = name.toUpperCase();
            if (!upper.endsWith('.XLSX')) return false;
            if (hasLocationSuffix(name)) return true; // L suffix = Store Sales (Location)
            if (upper.includes('INVENTORY REPORTS') || upper.includes('SKU')) return false;
            if (upper.includes('_JCP_') && upper.includes('_STORES_')) return true;
            if (/^\d+_STORES/i.test(name) && upper.includes('WEEKLY')) return true;
            if ((upper.includes('STORES') || upper.includes('WEEKLY SALES')) && hasWeekSuffix(name)) return true;
            return false;
          };
          
          // Sort all files by modified time descending
          const sorted = [...data.files].sort((a: DriveFile, b: DriveFile) => 
            new Date(b.modifiedTime).getTime() - new Date(a.modifiedTime).getTime()
          );
          
          // Find the most recent SKU and Store files using new patterns
          const skuFile = sorted.find((f: DriveFile) => isJcpSkuFile(f.name || ''));
          const storeFile = sorted.find((f: DriveFile) => isJcpStoreFile(f.name || ''));
          
          const newSelection = new Set<string>();
          if (skuFile) newSelection.add(skuFile.id);
          if (storeFile) newSelection.add(storeFile.id);
          setSelectedFiles(newSelection);
        } else {
          // For Ulta: extract date from filename (format: YYYY-MM-DD at end of filename)
          const extractDate = (filename: string): string | null => {
            const match = filename.match(/(\d{4}-\d{2}-\d{2})/);
            return match ? match[1] : null;
          };
          
          // Group files by date
          const filesByDate: Record<string, { sales?: DriveFile; store?: DriveFile }> = {};
          
          data.files.forEach((file: DriveFile) => {
            const date = extractDate(file.name);
            if (!date) return;
            
            if (!filesByDate[date]) {
              filesByDate[date] = {};
            }
            
            if (file.name.includes('Sales_Inv_Perf') || file.name.includes('Sales-Inv-Perf')) {
              if (!filesByDate[date].sales) {
                filesByDate[date].sales = file;
              }
            } else if (file.name.includes('Store-Sales') || file.name.includes('Store_Sales')) {
              if (!filesByDate[date].store) {
                filesByDate[date].store = file;
              }
            }
          });
          
          // Find the most recent date that has BOTH files
          const completeDates = Object.keys(filesByDate)
            .filter(date => filesByDate[date].sales && filesByDate[date].store)
            .sort()
            .reverse();
          
          const newSelection = new Set<string>();
          if (completeDates.length > 0) {
            const mostRecentDate = completeDates[0];
            const pair = filesByDate[mostRecentDate];
            if (pair.sales) newSelection.add(pair.sales.id);
            if (pair.store) newSelection.add(pair.store.id);
          }
          
          setSelectedFiles(newSelection);
        }
      } else {
        setSelectedFiles(new Set());
      }
    } catch (err: any) {
      setError(err.message);
      setDriveStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleFileSelection = (fileId: string) => {
    const newSelection = new Set(selectedFiles);
    if (newSelection.has(fileId)) {
      newSelection.delete(fileId);
    } else {
      newSelection.add(fileId);
    }
    setSelectedFiles(newSelection);
  };

  const handleSelectAll = () => {
    if (driveStatus?.files) {
      setSelectedFiles(new Set(driveStatus.files.map(f => f.id)));
    }
  };

  const handleDeselectAll = () => {
    setSelectedFiles(new Set());
  };

  const handleProcessData = async () => {
    if (selectedFiles.size === 0) {
      setError('Please select at least one file to process');
      return;
    }

    setProcessing(true);
    setError('');
    setProcessingResults([]);

    try {
      // Step 1: Check for duplicates (pass retailer context so API doesn't have to guess)
      const checkResponse = await fetch('/api/check-files-for-duplicates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileIds: Array.from(selectedFiles),
          retailer: activeRetailer  // 'Ulta', 'JCPenney', or 'Macys'
        })
      });

      if (!checkResponse.ok) {
        const errorData = await checkResponse.json();
        throw new Error(errorData.message || 'Failed to check files');
      }

      const checkData = await checkResponse.json();
      
      // If duplicates found, show confirmation modal
      if (checkData.hasDuplicates) {
        setDuplicateInfo(checkData.files.filter((f: any) => f.existsInDatabase));
        setShowConfirmModal(true);
        setProcessing(false); // Stop processing until user confirms
        return;
      }

      // No duplicates, proceed with processing
      await processFiles(false, []);

    } catch (err: any) {
      setError(err.message || 'Failed to check files');
      setProcessing(false);
    }
  };

  const processFiles = async (replaceExisting: boolean, fiscalWeeks: string[]) => {
    setProcessing(true);
    setError('');
    setProcessingResults([]);
    setShowConfirmModal(false);

    try {
      const processResponse = await fetch('/api/process-weekly-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileIds: Array.from(selectedFiles),
          replaceExisting,
          fiscalWeeks,
          retailer: activeRetailer  // Include the active retailer
        })
      });

      if (!processResponse.ok) {
        const errorData = await processResponse.json();
        throw new Error(errorData.message || 'Failed to process data');
      }

      const processData = await processResponse.json();
      setProcessingResults(processData.results);

      if (processData.success) {
        // Refresh drive status after successful processing
        await loadDriveStatus();
      } else {
        setError('Some processing steps failed. Check details below.');
      }

    } catch (err: any) {
      setError(err.message || 'Failed to process data');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmReplace = () => {
    const weeksToReplace = duplicateInfo.map(d => d.fiscalWeek);
    processFiles(true, weeksToReplace);
  };

  const handleCancelReplace = () => {
    setShowConfirmModal(false);
    setDuplicateInfo([]);
    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Weekly Data Monitor</h1>
              <p className="text-gray-600">
                Monitoring Google Drive for new {activeRetailer} data files
              </p>
            </div>
            <button
              onClick={loadDriveStatus}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Refresh status"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {/* Retailer Tabs */}
          <div className="mt-4 flex border-b border-gray-200">
            <button
              onClick={() => setActiveRetailer('Ulta')}
              className={`flex items-center gap-2 px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeRetailer === 'Ulta'
                  ? 'border-orange-500 text-orange-600 bg-orange-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
              Ulta Beauty
            </button>
            <button
              onClick={() => setActiveRetailer('Macys')}
              className={`flex items-center gap-2 px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeRetailer === 'Macys'
                  ? 'border-red-500 text-red-600 bg-red-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Store className="w-4 h-4" />
              Macy's
            </button>
            <button
              onClick={() => setActiveRetailer('JCPenney')}
              className={`flex items-center gap-2 px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeRetailer === 'JCPenney'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Store className="w-4 h-4" />
              JCPenney
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Google Drive Status */}
        {driveStatus && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {activeRetailer === 'Macys' ? 'Macy\'s' : 'Ulta'} Google Drive Folder
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                Last checked: {lastCheck.toLocaleTimeString()}
              </div>
            </div>
            
            {/* Show error if folder not configured */}
            {driveStatus.error && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800">Configuration Required</h3>
                    <p className="text-sm text-yellow-700 mt-1">{driveStatus.error}</p>
                    <p className="text-sm text-yellow-700 mt-2">
                      To set up {activeRetailer === 'Macys' ? "Macy's" : "JCPenney"} folder:
                    </p>
                    <ol className="text-sm text-yellow-700 list-decimal list-inside mt-1 space-y-1">
                      <li>Create a new Google Drive folder for {activeRetailer === 'Macys' ? "Macy's" : "JCPenney"} data</li>
                      <li>Share it with the service account</li>
                      <li>Add {activeRetailer === 'Macys' ? 'GOOGLE_DRIVE_MACYS_FOLDER_ID' : 'GOOGLE_DRIVE_JCP_FOLDER_ID'} to Vercel environment variables</li>
                      <li>Redeploy the application</li>
                    </ol>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FolderOpen className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium text-gray-900">Folder Status</h3>
                </div>
                <p className="text-sm text-gray-600">
                  {driveStatus.fileCount} Excel files found
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  ID: ...{driveStatus.folderId.slice(-8)}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  {driveStatus.hasRequiredFiles ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                  )}
                  <h3 className="font-medium text-gray-900">Required Files</h3>
                </div>
                <p className="text-sm text-gray-600">
                  {activeRetailer === 'Macys' 
                    ? (driveStatus.hasRequiredFiles ? 'Macy\'s files detected ✓' : 'No Macy\'s files found')
                    : (driveStatus.hasRequiredFiles ? 'Both files detected ✓' : 'Missing files')
                  }
                </p>
              </div>
            </div>

            {/* All Files with Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Files in Google Drive</h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleSelectAll}
                    disabled={processing}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
                  >
                    Select All
                  </button>
                  <span className="text-xs text-gray-400">|</span>
                  <button
                    onClick={handleDeselectAll}
                    disabled={processing}
                    className="text-xs text-gray-600 hover:text-gray-700 font-medium disabled:opacity-50"
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded p-2">
                {selectedFiles.size} of {driveStatus.files.length} files selected • Only checked files will be processed
              </div>
              
              {driveStatus.files.length === 0 ? (
                <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    No Excel files found in Google Drive folder
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {driveStatus.files.map((file) => {
                    const isSelected = selectedFiles.has(file.id);
                    
                    // Determine file type based on retailer and filename
                    let fileType: string;
                    const name = file.name.toUpperCase();
                    if (activeRetailer === 'Macys') {
                      if (name.includes('BUTTAH') && name.endsWith('.XLSX')) {
                        fileType = '🏬 Macy\'s SKU Detail';
                      } else {
                        fileType = '📄 Other File';
                      }
                    } else if (activeRetailer === 'JCPenney') {
                      // Helper: week suffix patterns _w##, _FW##, _FW_##
                      const hasWeekSuffix = (n: string): boolean => /_W\d+\.XLSX$/i.test(n) || /_FW_?\d+\.XLSX$/i.test(n);
                      // Check for "L" suffix before week (Location = Store Sales)
                      const hasLocationSuffix = (n: string): boolean => /L[_](?:FW[_]?|W)\d+\.XLSX$/i.test(n) || /L_W\d+\.XLSX$/i.test(n);
                      // SKU: Match patterns but exclude L suffix files (those are Store Sales)
                      const isSkuFile = !hasLocationSuffix(file.name) && (
                                        (name.includes('_JCP_') && name.includes('_SKU_')) || 
                                        (/^\d+_SKU/i.test(file.name) && name.includes('REPORT')) ||
                                        (name.includes('SKU') && hasWeekSuffix(file.name)) ||
                                        (name.includes('INVENTORY REPORTS') && hasWeekSuffix(file.name)) ||
                                        (/^\d+_/.test(file.name) && name.includes('INVENTORY REPORTS')));
                      // Store: L suffix files OR other store patterns (excluding Inventory Reports/SKU)
                      const isStoreFile = hasLocationSuffix(file.name) || (
                                          !name.includes('INVENTORY REPORTS') && !name.includes('SKU') && (
                                          (name.includes('_JCP_') && name.includes('_STORES_')) || 
                                          (/^\d+_STORES/i.test(file.name) && name.includes('WEEKLY')) ||
                                          ((name.includes('STORES') || name.includes('WEEKLY SALES')) && hasWeekSuffix(file.name))));
                      
                      if (isSkuFile) {
                        fileType = '📊 JCP SKU Detail';
                      } else if (isStoreFile) {
                        fileType = '🏪 JCP Store Sales';
                      } else {
                        fileType = '📄 Other File';
                      }
                    } else {
                      fileType = file.name.includes('Sales_Inv_Perf') || file.name.includes('Sales-Inv-Perf')
                        ? '📊 Sales Inventory Performance'
                        : file.name.includes('Store-Sales') || file.name.includes('Store_Sales')
                        ? '🏪 Store Sales'
                        : '📄 Other File';
                    }
                    
                    return (
                      <label
                        key={file.id}
                        className={`border rounded-lg p-3 flex items-center gap-3 cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-blue-400 bg-blue-50 shadow-sm' 
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleFileSelection(file.id)}
                          disabled={processing}
                          className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer disabled:opacity-50"
                        />
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {fileType}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 truncate">
                            {file.name}
                          </p>
                          <div className="flex gap-4 mt-1 text-xs text-gray-500">
                            <span>
                              📅 {new Date(file.createdTime).toLocaleDateString()} {new Date(file.createdTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span>
                              💾 {(parseInt(file.size) / 1024).toFixed(0)} KB
                            </span>
                          </div>
                        </div>
                        
                        {isSelected && (
                          <div className="flex-shrink-0">
                            <CheckCircle className="w-5 h-5 text-blue-600" />
                          </div>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Process Button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleProcessData}
                disabled={
                  processing || 
                  selectedFiles.size === 0 || 
                  (activeRetailer === 'Ulta' && !driveStatus.hasRequiredFiles) ||
                  !!driveStatus.error
                }
                className={`px-6 py-3 text-white rounded-lg font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 ${
                  activeRetailer === 'Macys' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <PlayCircle className="w-5 h-5" />
                    Process {activeRetailer} Data Now
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Processing Results */}
        {processingResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Processing Results</h2>
            <div className="space-y-3">
              {processingResults.map((result, idx) => (
                <div
                  key={idx}
                  className={`border rounded-lg p-4 ${
                    result.success
                      ? 'border-green-200 bg-green-50'
                      : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {result.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 capitalize">
                        {result.step.replace(/-/g, ' ')}
                      </h4>
                      {result.message && (
                        <p className="text-sm text-gray-600">{result.message}</p>
                      )}
                      {result.rowCount !== undefined && (
                        <p className="text-sm text-gray-600">{result.rowCount} rows processed</p>
                      )}
                      {result.error && (
                        <p className="text-sm text-red-700">{result.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-center">
              <a
                href="/dashboard"
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2"
              >
                <Database className="w-5 h-5" />
                View in Dashboard
              </a>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="font-medium text-blue-900 mb-2">How It Works - {activeRetailer}</h3>
          {activeRetailer === 'Ulta' && (
            <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
              <li>Drop new Excel files into the Google Drive folder (Ulta Weekly Data)</li>
              <li>System automatically detects the files within 1 minute</li>
              <li>Select <strong>both</strong> Sales Inventory Performance AND Store Sales files</li>
              <li>Click "Process Data Now" to download, transform, and load to database</li>
              <li>New fiscal week appears in dashboard automatically</li>
            </ol>
          )}
          {activeRetailer === 'Macys' && (
            <>
              <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                <li>Drop Macy's BUTTAH Excel files into the Macy's Google Drive folder</li>
                <li>System automatically detects the files within 1 minute</li>
                <li>Select the BUTTAH*.xlsx files you want to process</li>
                <li>Click "Process Data Now" to download, transform, and load to database</li>
                <li>New fiscal week appears in dashboard automatically</li>
              </ol>
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Macy's store sales data is not yet supported. Only SKU-level data will be processed.
                </p>
              </div>
            </>
          )}
          {activeRetailer === 'JCPenney' && (
            <>
              <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                <li>Drop JCP Excel files into the JCP Google Drive folder</li>
                <li>System automatically detects the files within 1 minute</li>
                <li>Select <strong>both</strong> JCP SKU AND Store files</li>
                <li>Click "Process Data Now" to download, transform, and load to database</li>
                <li>New fiscal week appears in dashboard automatically</li>
              </ol>
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm text-blue-800">
                  <strong>Supported file patterns:</strong><br/>
                  • SKU/Inventory: <code>*SKU*.xlsx</code>, <code>*Inventory Reports*.xlsx</code>, or <code>*_w##.xlsx</code> with Inventory Reports<br/>
                  • Store: <code>*Stores Weekly Sales*.xlsx</code> or <code>*_w##.xlsx</code> (without Inventory Reports)
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal for Duplicates */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Data Already Exists
                </h3>
                <p className="text-gray-700 mb-4">
                  The following fiscal weeks already exist in the database. Processing these files will <strong>REPLACE</strong> all existing data for these weeks.
                </p>
              </div>
            </div>

            {/* List of weeks that will be replaced */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 max-h-60 overflow-y-auto">
              <h4 className="font-medium text-yellow-900 mb-3">Weeks that will be replaced:</h4>
              <div className="space-y-3">
                {duplicateInfo.map((dup, idx) => (
                  <div key={idx} className="bg-white rounded p-3 border border-yellow-300">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">
                        Week {dup.fiscalWeek}
                      </span>
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                        {dup.fileName}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>• KPI Summary: {dup.rowCounts?.kpi || 0} rows will be deleted</div>
                      <div>• SKU Detail: {dup.rowCounts?.sku || 0} rows will be deleted</div>
                      <div>• Store Sales: {dup.rowCounts?.store || 0} rows will be deleted</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">
                <strong>⚠️ Warning:</strong> This action cannot be undone. Existing data for these weeks will be permanently deleted and replaced with the new files.
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelReplace}
                disabled={processing}
                className="px-6 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel - Keep Existing Data
              </button>
              <button
                onClick={handleConfirmReplace}
                disabled={processing}
                className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Replacing...
                  </>
                ) : (
                  <>
                    Replace Existing Data
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

